import { parseDeviceMessage } from './parse-device-message'
import { inferOsLabel } from './os-infer'
import { shortOsLabel } from './os-label'

// Builds the confirm-view device model from the auth-device /find response.
//
// New servers ship structured identity fields (clientLabel, ipLocation,
// deviceName, app, appVersion, devicePlatform, deviceOS, deviceType) filled
// from the requester's Edxr-User-Agent header. Prefer those: the device cell
// reads as name / app+version / OS instead of one long parsed string, and the
// icon is picked from the actual platform. Older servers only ship the
// "Confirm login for <client> at <location>" message, so the legacy parse +
// OS inference remain the fallback. Mirrors caffeine-viewer's
// CodeAuthenticatorUIV2 so the admin and the apps present identically.

export const APP_DISPLAY = {
  viewer: 'EducationXR Viewer',
  editor: 'EducationXR Editor'
}

export const PLATFORM_DISPLAY = {
  WebGLPlayer: 'Web browser',
  IPhonePlayer: 'iPhone / iPad',
  Android: 'Android device',
  OSXPlayer: 'Mac',
  WindowsPlayer: 'Windows PC',
  LinuxPlayer: 'Linux PC'
}

// Unity fills SystemInfo.deviceName with the literal "n/a" on WebGL — treat
// it (and blanks) as absent.
const clean = value => {
  const trimmed = (value || '').trim()
  if (!trimmed || trimmed.toLowerCase() === 'n/a') return null
  return trimmed
}

// Device family for icon selection: 'web' | 'phone' | 'tablet' | 'vr' | 'desktop'.
// Platform is authoritative when the server ships it; the device name
// disambiguates VR headsets (Quest reports the Android platform) and iPads
// (which report IPhonePlayer). Without a platform (legacy server) sniff the
// label text.
export const deviceFamilyFor = (platform, deviceName, label) => {
  const hint = ((deviceName || '') || (label || '')).toLowerCase()

  if (/quest|pico|vive|vision pro|focus/.test(hint)) return 'vr'

  switch (platform) {
    case 'WebGLPlayer': return 'web'
    case 'IPhonePlayer': return hint.includes('ipad') ? 'tablet' : 'phone'
    case 'Android': return hint.includes('tab') ? 'tablet' : 'phone'
    case 'OSXPlayer':
    case 'WindowsPlayer':
    case 'LinuxPlayer': return 'desktop'
    default: break
  }

  if (/ipad|tablet/.test(hint)) return 'tablet'
  if (/iphone|pixel|galaxy|android|phone/.test(hint)) return 'phone'
  if (/web|browser/.test(hint)) return 'web'
  return 'desktop'
}

// The maker's mark to sit inside the device outline, or null.
//
// Null is a normal answer: a logo nobody recognises is worse than no logo, and
// a wrong one is worse still, so this only answers when the platform is
// unambiguous and the outline carries the meaning alone the rest of the time.
//
// Order matters and mirrors the viewer's: Apple first, because macOS strings
// often also carry "x" or "intel" that a later rule would catch.
export const deviceBrandFor = (platform, os, deviceName, label) => {
  const hint = [platform, os, deviceName, label].filter(Boolean).join(' ').toLowerCase()

  // Headsets take no mark, and this has to be tested first: a Quest reports
  // the Android platform, so the android rule below would stamp a droid onto
  // the headset -- where it lands on the nose bridge between the lens holes.
  // Vision Pro is included even though Apple made it; the mark has nowhere to
  // sit on that outline whoever's logo it is.
  if (/quest|pico|vive|vision pro|focus/.test(hint)) return null

  if (/iphone|ipad|ios|mac|osx/.test(hint)) return 'apple'
  if (/android|pixel|galaxy/.test(hint)) return 'android'
  if (/windows|surface/.test(hint)) return 'windows'
  if (/linux|ubuntu/.test(hint)) return 'linux'

  return null
}

// Splits the one-line label an app sends when the structured fields are
// missing (an older build, or a request made without the Edxr-User-Agent
// header): "EducationXR Viewer 1.5.0 · Pixel 9 Pro Fold (Android OS 17 / API-37
// (CP2A.260705.006/15641320))" -> app, device, raw OS. The OS is greedy up to
// the last ")" because Android's own string carries parentheses. null when the
// label isn't in that shape (then it is shown whole, as before).
export const splitClientLabel = label => {
  const m = /^(.+?) · (.+?) \((.+)\)$/.exec(label || '')
  if (!m) return null
  return { app: m[1].trim(), device: m[2].trim(), os: m[3].trim() }
}

export const deviceIdentityFrom = result => {
  const clientLabel = clean(result && result.clientLabel)

  if (!clientLabel) {
    // Legacy server: parse the message, keep the single-line presentation.
    const { deviceLabel, location } = parseDeviceMessage(result && result.message)
    return {
      label: deviceLabel,
      app: null,
      os: inferOsLabel(deviceLabel),
      location,
      family: deviceFamilyFor(null, null, deviceLabel),
      brand: deviceBrandFor(null, null, null, deviceLabel)
    }
  }

  const deviceName = clean(result.deviceName)
  // Without structured fields, read them out of the one-line label instead of
  // showing all of it as the device's name.
  const parts = deviceName || result.devicePlatform ? null : splitClientLabel(clientLabel)
  const label = deviceName || PLATFORM_DISPLAY[result.devicePlatform] || (parts && parts.device) || clientLabel

  const appDisplay = APP_DISPLAY[(result.app || '').toLowerCase()] || clean(result.app)
  const version = clean(result.appVersion)
  const app = appDisplay ? (version ? `${appDisplay} ${version}` : appDisplay) : (parts && parts.app)

  // Shortened for display only. deviceOS is SystemInfo.operatingSystem verbatim
  // -- "Android OS 17 / API-37 (CP2A.260705.006/15641320)" -- and the full
  // string still travels in the payload the server records. The person deciding
  // whether to approve a sign-in needs to recognise the device, not diagnose it.
  const os = shortOsLabel(clean(result.deviceOS)) || (parts && shortOsLabel(parts.os)) || inferOsLabel(clientLabel)
  const location = clean(result.ipLocation) || parseDeviceMessage(result.message).location

  return {
    label,
    app,
    os,
    location,
    family: deviceFamilyFor(result.devicePlatform, deviceName, clientLabel),
    brand: deviceBrandFor(result.devicePlatform, os, deviceName, clientLabel)
  }
}
